/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { Link } from 'react-router-dom';
import { useSEOHead } from '../../../hooks/useSEOHead';
import { competitorPages } from '../../../mocks/competitorPages';
import Breadcrumb from '../../../components/feature/Breadcrumb';

export default function VsOceancrestChevroletPage() {
  const pageData = competitorPages.find(p => p.slug === 'oceancrest-chevrolet');

  useSEOHead({
    title: pageData?.metaTitle ?? 'vs Oceancrest Chevrolet | Rockhound Auto',
    description: 'See how Rockhound Auto compares to Oceancrest Chevrolet in Warrenton, OR. Transparent pricing, no hidden fees, and access to every make — not just Chevrolet.',
    keywords: 'Oceancrest Chevrolet alternative, auto broker Warrenton OR, used cars vs Oceancrest, Oregon Coast car buying',
    canonicalUrl: 'https://rockhoundauto.com/vs/oceancrest-chevrolet',
  });

  if (!pageData) return null;

  return (
    <>
      <Breadcrumb 
        items={[
          { label: 'Compare', path: '/compare' },
          { label: 'vs Oceancrest Chevrolet' }
        ]} 
      />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-24 lg:py-32">
        <div className="absolute inset-0 bg-black/30" />
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("https://public.readdy.ai/ai/img_res/16e33245a24cfc2f7e704b3d02448b3e.jpg")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-500/90 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="ri-contrast-2-line" />
            The Honest Comparison
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
            Oceancrest Chevrolet Sells One Brand.<br />
            <span className="text-amber-400">We Sell Every Option.</span>
          </h1>
          <p className="text-xl sm:text-2xl text-slate-200 mb-4 leading-relaxed max-w-3xl mx-auto">
            Single-brand dealerships limit your choices. We open them up — with access to every make and model across the Pacific Northwest.
          </p>
          <p className="text-base text-slate-400 mb-10 max-w-2xl mx-auto">
            Transparent pricing, zero pressure, and savings that average $3,500–$8,900 per deal.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer shadow-lg"
            >
              <i className="ri-message-3-line" />
              Get a Free Quote
            </Link>
            <a
              href="tel:+18554686348"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line" />
              (855) 468-6348
            </a>
          </div>
        </div>
      </section>

      {/* Side-by-Side Comparison */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Head-to-Head Comparison</h2>
            <p className="text-lg text-gray-600">Understanding your options before you buy</p>
          </div>
          
          <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="p-5 text-left font-semibold text-slate-600 border-b border-slate-200 text-sm uppercase tracking-wide">Feature</th>
                  <th className="p-5 text-center font-bold text-emerald-700 border-b border-emerald-200 bg-emerald-50 text-sm uppercase tracking-wide">Rockhound Auto</th>
                  <th className="p-5 text-center font-semibold text-slate-500 border-b border-slate-200 text-sm uppercase tracking-wide">Oceancrest Chevrolet</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Vehicle Selection', 'Every make & model', 'Chevrolet only'],
                  ['Pricing Model', 'Transparent, itemized', 'Traditional dealership'],
                  ['Sales Approach', 'Zero pressure', 'Time-limited offers'],
                  ['Trade-In Process', 'Multiple competitive offers', 'Single offer'],
                  ['Inventory Access', 'Regional dealer network', 'On-lot inventory'],
                  ['Negotiation', 'We negotiate for you', 'You negotiate alone'],
                  ['Add-Ons', 'Your choice, always', 'Often pre-installed'],
                ].map(([feature, us, them], i) => (
                  <tr key={feature} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                    <td className="p-4 font-medium text-slate-700 border-b border-slate-100">{feature}</td>
                    <td className="p-4 text-center bg-emerald-50/60 border-b border-emerald-100">
                      <div className="flex items-center justify-center gap-2">
                        <i className="ri-check-line text-emerald-600 text-lg"></i>
                        <span className="font-semibold text-emerald-800 text-sm">{us}</span>
                      </div>
                    </td>
                    <td className="p-4 text-center border-b border-slate-100">
                      <div className="flex items-center justify-center gap-2">
                        <i className="ri-close-line text-red-400 text-lg"></i>
                        <span className="text-slate-500 text-sm">{them}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* What to Know Before You Visit */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/40 text-emerald-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-lightbulb-line"></i> Buyer Education
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">What to Know Before You Visit</h2>
            <p className="text-lg text-gray-600">
              Understanding common dealership practices helps you make informed decisions
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-car-line text-3xl text-slate-600"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-slate-800">Single-Brand Limitation</h3>
              <p className="text-slate-600 leading-relaxed mb-3">
                What if you want to compare a Chevrolet Silverado against a Ford F-150, Toyota Tundra, or Ram 1500? Single-brand dealerships can only show you one option.
              </p>
              <div className="bg-emerald-50 border-l-4 border-emerald-500 pl-3 py-2 rounded-r-lg">
                <p className="text-sm text-emerald-800 leading-relaxed">
                  💡 <strong>Our approach:</strong> We give you access to every make and model from dealers across Oregon and Washington, so you can make the best choice for your needs.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-3xl text-slate-600"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-slate-800">Market Adjustments</h3>
              <p className="text-slate-600 leading-relaxed mb-3">
                "Market adjustments" of $3,000–$5,000 above MSRP are common on popular models. These markups are pure profit and often negotiable — but only if you know to ask.
              </p>
              <div className="bg-emerald-50 border-l-4 border-emerald-500 pl-3 py-2 rounded-r-lg">
                <p className="text-sm text-emerald-800 leading-relaxed">
                  💡 <strong>Our approach:</strong> We negotiate on your behalf to eliminate unnecessary markups and get you the fairest price possible.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-exchange-dollar-line text-3xl text-slate-600"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-slate-800">Trade-In Valuation</h3>
              <p className="text-slate-600 leading-relaxed mb-3">
                Single-dealer trade-in offers can be $2,500–$4,500 below market value. Without competition, there's less incentive to offer fair value.
              </p>
              <div className="bg-emerald-50 border-l-4 border-emerald-500 pl-3 py-2 rounded-r-lg">
                <p className="text-sm text-emerald-800 leading-relaxed">
                  💡 <strong>Our approach:</strong> We leverage competition between multiple dealers to get you fair market value — often $3,000–$5,000 more.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-file-list-3-line text-3xl text-slate-600"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-slate-800">Add-On Products</h3>
              <p className="text-slate-600 leading-relaxed mb-3">
                Paint protection, fabric coating, nitrogen tire fill, and VIN etching can add $2,000–$3,000 to your final price. These are often presented as mandatory but are actually optional.
              </p>
              <div className="bg-emerald-50 border-l-4 border-emerald-500 pl-3 py-2 rounded-r-lg">
                <p className="text-sm text-emerald-800 leading-relaxed">
                  💡 <strong>Our approach:</strong> Everything is optional. You choose what you want, and we ensure transparent pricing with no surprises.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Real People. Real Savings.</h2>
            <p className="text-lg text-gray-600">Customers who chose Rockhound Auto for their vehicle purchase</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'James T.',
                location: 'Warrenton, OR',
                vehicle: '2023 Chevrolet Silverado',
                saved: '$7,200',
                quote: 'I was quoted $54,000 for a Silverado with a $4,500 market adjustment. Rockhound found me the same truck for $46,800 with zero markup. That\'s real money back in my pocket.',
              },
              {
                name: 'Linda M.',
                location: 'Astoria, OR',
                vehicle: '2024 Honda CR-V',
                saved: '$5,400',
                quote: 'I wanted to compare a Chevy Equinox against a Honda CR-V. Rockhound showed me both, helped me decide, and saved me $5,400 on the Honda. Best decision I made.',
              },
              {
                name: 'Carlos R.',
                location: 'Seaside, OR',
                vehicle: '2023 Toyota Tacoma',
                saved: '$6,800',
                quote: 'The trade-in offer I got was $9,000. Rockhound got me $13,500 for the same truck, then found me a Tacoma for $2,300 less than I was quoted elsewhere. Total savings: $6,800.',
              },
            ].map((t, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-8 shadow-sm border border-slate-100 flex flex-col">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, s) => (
                    <i key={s} className="ri-star-fill text-amber-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-slate-700 italic leading-relaxed flex-1 mb-6">"{t.quote}"</p>
                <div className="border-t border-slate-200 pt-4">
                  <p className="font-bold text-slate-800">{t.name}</p>
                  <p className="text-slate-500 text-sm">{t.location} · {t.vehicle}</p>
                  <p className="text-emerald-600 font-bold mt-1">Saved: {t.saved}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-teal-600 via-teal-700 to-slate-800 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-extrabold mb-4">
            Ready to Explore All Your Options?
          </h2>
          <p className="text-xl text-teal-100 mb-3 leading-relaxed">
            Stop limiting yourself to one brand. Get access to every make and model with transparent pricing and zero pressure.
          </p>
          <p className="text-base text-teal-200 mb-10">
            Serving Warrenton, Astoria, Seaside, and the entire Oregon Coast.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-white text-teal-700 hover:bg-teal-50 px-8 py-4 rounded-lg font-bold transition-all whitespace-nowrap cursor-pointer shadow-lg text-lg"
            >
              <i className="ri-message-3-line" />
              Get Your Free Quote
            </Link>
            <a
              href="tel:+18554686348"
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white px-8 py-4 rounded-lg font-bold transition-all whitespace-nowrap cursor-pointer text-lg"
            >
              <i className="ri-phone-line" />
              (855) 468-6348
            </a>
          </div>
        </div>
      </section>
    </>
  );
}